var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Button, createStyleSheet, Focusable, IconButton, Text, useStyleSheet, View } from '@gopeerproject/ui-kit';
import React, { useEffect, useCallback, useRef, useState } from 'react';
import { fetchAudio, getTimeline, getWaveTimeline, STEP_MIN_HEIGHT, STEPS } from '../helpers';
import { Header } from '.';
import { FileDownloader } from '../../../components/FileDownloader';
import throttle from 'lodash/throttle';
import { humanizeFileSize } from '../../../helpers';
export const Audio = ({ file, onClose }) => {
    var _a;
    const styles = useStyleSheet(getStyles);
    const mounted = useRef(false);
    const players = useRef([]);
    const blobRef = useRef(null);
    const [audioTimeline, setAudioTimeline] = useState(getWaveTimeline(0));
    const [isBuffering, setIsBuffering] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [hoveredTime, setHoveredTime] = useState(null);
    const [objectUrls, setObjectUrls] = useState([]);
    const [revokableUrls, setRevokableUrls] = useState([]);
    const passedSteps = Math.ceil((currentTime / duration) * STEPS);
    const hoveredSteps = ((hoveredTime !== null && hoveredTime !== void 0 ? hoveredTime : 0) / duration) * STEPS;
    const time = hoveredTime !== null ? hoveredTime : currentTime;
    const [chunkIndex, setChunk] = useState(0);
    const loadingAnimation = useRef(0);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    useEffect(() => {
        if (isFetching) {
            const interval = setInterval(() => {
                setAudioTimeline(getWaveTimeline(++loadingAnimation.current));
            }, 125);
            return () => {
                clearInterval(interval);
            };
        }
        return;
    }, [isFetching]);
    useEffect(() => {
        void fetchAudio(file.data, chunkIndex).then(({ data, isMore }) => __awaiter(void 0, void 0, void 0, function* () {
            if (!mounted.current)
                return;
            const blob = new Blob(blobRef.current ? [blobRef.current, data] : [data]);
            blobRef.current = blob;
            const objectUrl = URL.createObjectURL(blob);
            setObjectUrls((o) => o.concat(objectUrl));
            if (isMore) {
                setChunk(chunkIndex + 1);
                return;
            }
            blobRef.current = null;
            const audioTimeline = yield getTimeline(blob);
            setIsFetching(false);
            setAudioTimeline(audioTimeline);
        }));
    }, [chunkIndex, file.data]);
    const getCurrentPlayer = () => {
        var _a;
        return (_a = players.current.find((v) => !v.paused)) !== null && _a !== void 0 ? _a : players.current[players.current.length - 1];
    };
    useEffect(() => {
        if (isBuffering && currentTime) {
            const player = getCurrentPlayer();
            if (player) {
                player.currentTime = currentTime;
                void player.play();
                setIsBuffering(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUrls]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedRewind = useCallback(throttle((e) => {
        var _a;
        if (isBuffering || !players.current.length || !e)
            return;
        if (e.type === 'mouseleave') {
            setHoveredTime(null);
            return;
        }
        const pageX = e.nativeEvent.pageX;
        const { offsetLeft, offsetWidth } = e.target;
        const timeToSet = offsetLeft >= pageX
            ? 0
            : ((pageX - offsetLeft) / offsetWidth) * duration;
        const { buttons } = e;
        setHoveredTime(timeToSet);
        if (buttons === 1 || e.type === 'click') {
            (_a = getCurrentPlayer()) === null || _a === void 0 ? void 0 : _a.pause();
            setCurrentTime(timeToSet);
            const player = players.current[players.current.length - 1];
            if (player) {
                player.currentTime = timeToSet;
                if (!isPlaying && buttons !== 1)
                    void player.play();
            }
        }
    }, 100), [duration, isBuffering]);
    const disposeObjectUrls = () => {
        const revokable = [];
        setObjectUrls(objectUrls.map((v, i) => {
            const player = players.current[i];
            if (i === objectUrls.length - 1 || (player && !player.paused))
                return v;
            if (v)
                revokable.push(v);
            return null;
        }));
        setRevokableUrls(revokable);
    };
    useEffect(() => {
        if (revokableUrls.length) {
            revokableUrls.forEach((v) => {
                URL.revokeObjectURL(v);
            });
            setRevokableUrls([]);
        }
    }, [revokableUrls]);
    const updatePlaying = () => {
        setIsPlaying(players.current.some((v) => !v.paused));
    };
    const onAbort = (i) => () => {
        if (i !== objectUrls.length - 1) {
            const player = players.current[players.current.length - 1];
            if (player) {
                player.currentTime = currentTime;
                void player.play();
            }
            disposeObjectUrls();
        }
        else if (isFetching)
            setIsBuffering(true);
    };
    const onTimeUpdate = (i) => (e) => {
        const { currentTime, duration } = e.target;
        if (!isBuffering &&
            ((i === objectUrls.length - 1 && !isFetching) ||
                currentTime !== duration))
            setCurrentTime(currentTime);
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Header, { file: file, onClose: onClose, isFixed: true }),
        React.createElement(View, { style: styles.container },
            React.createElement(View, { style: styles.playerContainer },
                React.createElement(IconButton, { isLoading: isBuffering || !objectUrls.length, type: 'tertiary', size: 'sm', accent: 'neutral', active: true, value: isPlaying ? 'pause' : 'play', onPress: () => {
                        if (isBuffering || !objectUrls.length)
                            return;
                        const player = getCurrentPlayer();
                        if (player) {
                            if (isPlaying) {
                                player.pause();
                                return;
                            }
                            player.currentTime = currentTime;
                            void player.play();
                        }
                    } }),
                React.createElement(Focusable
                // @ts-expect-error this works so relax
                , { 
                    // @ts-expect-error this works so relax
                    onMouseMove: debouncedRewind, onMouseLeave: debouncedRewind, onPress: debouncedRewind, style: styles.stepContainer }, audioTimeline.map((v, i) => (React.createElement(View, { key: i, style: [
                        styles.step,
                        { height: v + STEP_MIN_HEIGHT },
                        passedSteps > i && styles.passedStep,
                        hoveredSteps > i && styles.hoveredStep,
                        passedSteps > i &&
                            hoveredSteps > i &&
                            styles.hoveredPassedStep
                    ] })))),
                objectUrls.map((v, i) => v && (React.createElement("audio", { key: i, ref: (el) => el && (players.current[i] = el), src: v, onPlay: updatePlaying, onPause: updatePlaying, onEnded: onAbort(i), onAbort: onAbort(i), onTimeUpdate: onTimeUpdate(i), onDurationChange: (e) => {
                        setDuration(e.target.duration);
                    } }))),
                React.createElement(Text, { type: 'body', size: 'md', weight: 'regular', style: styles.currentTime },
                    Math.floor(time / 60),
                    ":",
                    String(Math.round(time % 60)).padStart(2, '0'))),
            React.createElement(Text, { type: 'body', size: 'lg', weight: 'semibold' }, file.name.slice(0, file.name.lastIndexOf('.'))),
            React.createElement(Text, { type: 'body', size: 'md', weight: 'regular', style: styles.details },
                ((_a = file.metadata) === null || _a === void 0 ? void 0 : _a.size) && `${humanizeFileSize(file.metadata.size)} ⋅ `,
                file.name.slice(file.name.lastIndexOf('.') + 1).toUpperCase()),
            React.createElement(FileDownloader, { name: file.name, href: file.data }, (download) => (React.createElement(Button, { leftIcon: 'arrow-download', type: 'primary', accent: 'default', size: 'lg', text: 'Download', onPress: download }))))));
};
const getStyles = createStyleSheet(({ color, size }) => ({
    container: {
        justifyContent: 'center',
        alignItems: 'center',
        flexGrow: 1,
        // @ts-expect-error perfectly valid position
        position: 'unset'
    },
    playerContainer: {
        marginBottom: size.K,
        padding: size.E,
        borderRadius: size.G,
        backgroundColor: color.white.p4,
        flexDirection: 'row',
        alignItems: 'center',
        // @ts-expect-error perfectly valid position
        position: 'unset'
    },
    stepContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'stretch',
        marginHorizontal: size.G,
        gap: size.A,
        overflow: 'hidden'
    },
    details: { color: color.white.p56, marginTop: size.B, marginBottom: size.K },
    step: {
        width: 3,
        borderRadius: size.K,
        backgroundColor: color.white.p16,
        pointerEvents: 'none',
        transitionProperty: ['height', 'background-color'],
        transitionTimingFunction: ['linear', 'ease']
    },
    passedStep: { backgroundColor: color.primary.solid },
    hoveredStep: { backgroundColor: color.primary.solid },
    hoveredPassedStep: { backgroundColor: color.primary.solidHover },
    currentTime: { marginRight: size.C, width: size.L }
}));
//# sourceMappingURL=Audio.js.map